---
title: Bring Structure to Your Agent Workflows
description: Three new commands in the Entire CLI — summarize agent activity, run review skills against your branch, and explore experimental workflows.
category: Changelog
author: Marvin
---

{/* Editorial update notice, not a section title. */}
{/* lint ignore no-emphasis-as-heading */}

_This blog was modified slightly on Aug 26, 2026._

This week, we added three new commands that make it easier to review agent-generated code: `entire recap`, `entire review`, and `entire labs`.

## Recap Recent Agent Activity

Let's begin with `entire recap`, a new command that shows how you and your team are using AI across your repositories, broken down by agent. Choose a time window with `--day` (the default), `--week`, `--month`, or a rolling `--90` days. Use `--view you|team|both` to switch between your activity, team activity, or a side-by-side comparison, and `--agent` to filter to a specific agent.

As soon as you're signed in, `entire recap` opens an interactive terminal by default, giving each agent a panel with sessions, checkpoints, token usage, files touched, top skills and MCP servers, and tool mix. Or, if you prefer plain output suitable for scripts or CI, you can use `--static` to run the command.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "67.08074534161491%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/65415e57a6d7b2d797be3c08fe7e9dfc/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F65415e57a6d7b2d797be3c08fe7e9dfc%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Entire recap demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

## Review Your Branch with Multiple Agents

The new `entire review` command uses your configured AI reviewers to check the code changes on your current branch.

On the first run, an interactive picker writes `.entire/settings.json` with the skills and per-agent prompts to use. Then, when a review starts, Entire shows the scope and adds the checkpoint history for each commit in scope to the agent prompt. That gives the reviewer the context behind the changes before it evaluates them.

You can configure more than one agent, including Claude Code and Codex, to run reviews in parallel, and you can always use `--edit` to reopen the picker or `--agent NAME` to override the default agent. `entire review` runs agent reviews concurrently against the same scope and shows the results side by side in a live terminal. Afterward, the command records the review in the next checkpoint so its metadata stays with the commit, giving you a record of reviews for posterity.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "63.52941176470588%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/fc7c27edc0a1d56466182d6e811516c2/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2Ffc7c27edc0a1d56466182d6e811516c2%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Entire review demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

## Run Labs to Try Experimental Workflows

Finally, we've introduced a command called `entire labs` that lists experimental Entire workflows for you to explore.

Run it to see which experimental commands are currently available, and deploy them in your branch to see how they work in practice. You may even find a sneak peek inside: future experimental workflows will appear under this command even while they're still in development.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "70.03891050583657%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/b841816d1fc77b7d0dc6f9f7f56be214/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2Fb841816d1fc77b7d0dc6f9f7f56be214%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Entire labs demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

## Get Started

Update the CLI to version 0.6.1 or later, then run these commands from any enabled repository:

```bash
entire review --help
entire recap
entire labs
```
